import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Shield, Target, AlertTriangle } from 'lucide-react';
import { metricTile } from '@/lib/metricTileStyles';

interface InvestmentGradeAnalyticsProps {
  trendAnalysis: {
    growth_5yr: number;
    growth_10yr: number;
    volatility_index: number;
    recession_impact_score: number;
    peak_year: number;
    trough_year: number;
  };
  occupationTitle: string;
  className?: string;
}

const InvestmentGradeAnalytics: React.FC<InvestmentGradeAnalyticsProps> = ({
  trendAnalysis,
  occupationTitle,
  className = ''
}) => {
  // Risk assessment scoring
  const getRiskScore = () => {
    const volatilityWeight = 0.4;
    const recessionWeight = 0.3;
    const growthWeight = 0.3;
    
    const volatilityScore = Math.max(0, 100 - (trendAnalysis.volatility_index * 1000));
    const recessionScore = Math.max(0, 100 + (trendAnalysis.recession_impact_score * 200));
    const growthScore = Math.min(100, Math.max(0, 50 + (trendAnalysis.growth_5yr * 2)));
    
    return Math.round(
      volatilityScore * volatilityWeight + 
      recessionScore * recessionWeight + 
      growthScore * growthWeight
    );
  };

  const getRiskLevel = (score: number) => {
    if (score >= 80) return { level: 'Conservative', color: metricTile.green.icon, bg: metricTile.green.surface };
    if (score >= 60) return { level: 'Moderate', color: metricTile.blue.icon, bg: metricTile.blue.surface };
    if (score >= 40) return { level: 'Growth', color: metricTile.yellow.icon, bg: metricTile.yellow.surface };
    return { level: 'Aggressive', color: metricTile.red.icon, bg: metricTile.red.surface };
  };

  const getMarketTiming = () => {
    const currentGrowth = trendAnalysis.growth_5yr;
    const volatility = trendAnalysis.volatility_index;
    
    if (currentGrowth > 0.05 && volatility < 0.15) {
      return { timing: 'Optimal Entry', color: metricTile.green.icon, description: 'Strong growth with stable conditions' };
    }
    if (currentGrowth > 0.02) {
      return { timing: 'Good Timing', color: metricTile.blue.icon, description: 'Positive momentum detected' };
    }
    if (currentGrowth > 0) {
      return { timing: 'Cautious', color: metricTile.yellow.icon, description: 'Moderate growth, monitor trends' };
    }
    return { timing: 'Hold/Monitor', color: metricTile.red.icon, description: 'Consider alternative opportunities' };
  };

  const riskScore = getRiskScore();
  const riskLevel = getRiskLevel(riskScore);
  const marketTiming = getMarketTiming();

  return (
    <Card className={`shadow-lg ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Investment-Grade Risk Analysis
          </CardTitle>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Executive Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg ${riskLevel.bg}`}>
            <div className="flex items-center justify-between mb-2">
              <Shield className={`h-4 w-4 ${riskLevel.color}`} />
              <Badge variant="outline" className={riskLevel.color}>
                {riskLevel.level}
              </Badge>
            </div>
            <div className="text-2xl font-bold mb-1 text-foreground">{riskScore}/100</div>
            <div className="text-sm font-medium text-foreground">Risk Score</div>
            <p className="text-xs text-muted-foreground mt-1">
              Investment stability rating
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between mb-2">
              <Target className={`h-4 w-4 ${marketTiming.color}`} />
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold mb-1 text-foreground">{marketTiming.timing}</div>
            <div className="text-sm font-medium text-foreground">Market Timing</div>
            <p className="text-xs text-muted-foreground mt-1">
              {marketTiming.description}
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <Shield className="h-4 w-4 text-primary" />
            </div>
            <div className="text-lg font-bold mb-1 text-foreground">
              {trendAnalysis.recession_impact_score > -0.05 ? 'Resilient' : 'Cyclical'}
            </div>
            <div className="text-sm font-medium text-foreground">Recession Profile</div>
            <p className="text-xs text-muted-foreground mt-1">
              Economic downturn behavior
            </p>
          </div>
        </div>

        {/* Investment Thesis */}
        <div className="space-y-4">
          <h4 className="font-semibold">Investment Thesis</h4>
          
          <div className="space-y-3">
            <div className="p-3 bg-muted/20 rounded-lg">
              <h5 className="font-medium text-sm mb-1">Growth Profile</h5>
              <p className="text-sm text-muted-foreground">
                {trendAnalysis.growth_5yr > 0.05 
                  ? `Strong growth trajectory with ${(trendAnalysis.growth_5yr * 100).toFixed(1)}% 5-year CAGR, positioning ${occupationTitle} as a growth opportunity.`
                  : trendAnalysis.growth_5yr > 0.02
                  ? `Moderate growth at ${(trendAnalysis.growth_5yr * 100).toFixed(1)}% 5-year CAGR, indicating stable expansion for ${occupationTitle}.`
                  : `Conservative growth profile requires careful market timing and risk management for ${occupationTitle}.`
                }
              </p>
            </div>

            <div className="p-3 bg-muted/20 rounded-lg">
              <h5 className="font-medium text-sm mb-1">Volatility Assessment</h5>
              <p className="text-sm text-muted-foreground">
                {trendAnalysis.volatility_index < 0.1 
                  ? `Low volatility (${(trendAnalysis.volatility_index * 100).toFixed(1)}%) indicates predictable career progression and stable earning potential.`
                  : trendAnalysis.volatility_index < 0.2
                  ? `Moderate volatility (${(trendAnalysis.volatility_index * 100).toFixed(1)}%) suggests balanced risk-reward profile with manageable fluctuations.`
                  : `High volatility (${(trendAnalysis.volatility_index * 100).toFixed(1)}%) requires active career management and diversification strategies.`
                }
              </p>
            </div>

            <div className="p-3 bg-muted/20 rounded-lg">
              <h5 className="font-medium text-sm mb-1">Economic Cycle Positioning</h5>
              <p className="text-sm text-muted-foreground">
                Historical peak in {trendAnalysis.peak_year}, trough in {trendAnalysis.trough_year}. 
                {trendAnalysis.recession_impact_score > -0.02 
                  ? ' Demonstrates strong recession resilience, making it suitable for conservative portfolios.'
                  : ' Shows cyclical sensitivity, requiring tactical allocation and timing consideration.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Recommendation */}
        <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border-l-4 border-primary">
          <h5 className="font-semibold text-primary mb-2">Professional Recommendation</h5>
          <p className="text-sm">
            Based on quantitative analysis, {occupationTitle} presents a <strong>{riskLevel.level.toLowerCase()}</strong> risk profile 
            with <strong>{marketTiming.timing.toLowerCase()}</strong> market conditions. 
            {riskScore >= 70 
              ? ' Recommended for core career allocation with stable growth expectations.'
              : riskScore >= 50
              ? ' Suitable for balanced career strategy with moderate growth potential.'
              : ' Consider for opportunistic allocation with active risk management.'
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestmentGradeAnalytics;